import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import CustomModal from "../DealTracker/CustomModal";
import { CrossIcon, SpinnerIcon } from "../Icons";
import InputWithLabel from "../InputWithLabel";
import CountryDialCodeSelect from "../Messages/CountryDialCodeSelect";
import Dropdown from "../DropDown";
import countries from "../../constants/countries.json";
import { useOnboardClientMutation } from "../../store/apiSlices/adminApi";
import { toast } from "react-toastify";

type OnboardClientForm = {
  first_name: string;
  last_name: string;
  company_name: string;
  work_email: string;
  dialCode: string;
  company_phone_number: string;
  company_reg_country: string;
};

export default function OnboardClientBtn() {
  const [isOpen, setIsOpen] = useState(false);
  const toggleModal = () => setIsOpen((prev) => !prev);

  const [onboardClient] = useOnboardClientMutation();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<OnboardClientForm>({
    defaultValues: {
      first_name: "",
      last_name: "",
      company_name: "",
      work_email: "",
      dialCode: "+91",
      company_phone_number: "",
      company_reg_country: "",
    },
  });

  const onSubmit = async (values: OnboardClientForm) => {
    // console.log("Onboard Client Data:", values);

    const { data } = await onboardClient(values);

    if (data.status === "success") {
      toast.success(data?.message || "Client created successfully!");
      reset();
      setIsOpen(false);
    } else {
      toast.error(data?.message || "Something went wrong, please try again!");
    }
  };

  return (
    <div>
      <button
        className="h-10 px-4 bg-custom-blue text-white rounded-md hover:bg-blue-600 transition"
        onClick={toggleModal}
      >
        Onboard Client
      </button>

      <CustomModal
        open={isOpen}
        onClose={toggleModal}
        className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full"
      >
        <button type="button" className="float-end" onClick={toggleModal}>
          <CrossIcon className="size-8 text-[#1A1D21]" />
        </button>

        <div className="mb-4">
          <h2 className="text-lg text-gray-800 font-medium">Onboard Client</h2>
          <p className="text-xs text-gray-600">
            Enter complete client and company information to onboard partners
            efficiently and maintain accurate business documentation.
          </p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid grid-cols-2 gap-2"
        >
          {/* First Name */}
          <InputWithLabel
            label="First Name"
            placeholder="Enter here"
            {...register("first_name", { required: "First name is required" })}
            error={errors.first_name?.message}
          />

          {/* Last Name */}
          <InputWithLabel
            label="Last Name"
            placeholder="Enter here"
            {...register("last_name", { required: "Last name is required" })}
            error={errors.last_name?.message}
          />

          {/* Company Name */}
          <InputWithLabel
            label="Company Name"
            placeholder="Enter here"
            {...register("company_name", {
              required: "Company name is required",
            })}
            error={errors.company_name?.message}
          />

          {/* Company Registration Country */}
          <Dropdown
            label="Company Registration Country"
            placeholder="Select a country"
            options={countries.map((c) => ({ label: c.name, value: c.iso2 }))}
            {...register("company_reg_country", {
              required: "Registration country is required",
            })}
            error={errors.company_reg_country?.message}
          />

          {/* Work Email */}
          <InputWithLabel
            label="Work Email"
            placeholder="Enter here"
            {...register("work_email", {
              required: "Work email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
              },
            })}
            error={errors.work_email?.message}
          />

          {/* Company Phone Number + Dial Code */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              Business Phone Number
            </label>
            <div className="flex gap-1">
              <Controller
                name="dialCode"
                control={control}
                render={({ field }) => (
                  <div className="w-[100px]">
                    <CountryDialCodeSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
              <InputWithLabel
                label=""
                placeholder="Enter phone number"
                {...register("company_phone_number", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^\+?\d{0,3}?[-.\s()]?\d{3,15}$/,
                    message: "Enter a valid phone number",
                  },
                  validate: (value) => {
                    const digitsOnly = value.replace(/\D/g, "");
                    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
                      return "Phone number must be between 7 and 15 digits";
                    }
                    return true;
                  },
                })}
                error={errors.company_phone_number?.message}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-2 col-span-full text-end">
            <button
              type="submit"
              className="bg-custom-blue-medium px-4 py-2 text-white rounded-md font-medium hover:bg-blue-600 transition disabled:bg-custom-blue-medium/70"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <SpinnerIcon className="size-5 text-gray-200 animate-spin fill-custom-blue-medium" />
              ) : (
                "Send Invite"
              )}
            </button>
          </div>
        </form>
      </CustomModal>
    </div>
  );
}
